/**
 * Helper to integrate native Capacitor Camera picker with Ant Design Upload component
 * This allows using ImgCrop on both web and mobile by intercepting the file input click
 */
import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';

/**
 * Setup native file picker integration for Ant Design Upload
 * Call this in useEffect to override the file input behavior on native platforms
 * @param {Function} onFileSelected - Callback when file is selected
 * @returns {Function} Cleanup function
 */
export const setupNativeUploadIntegration = (onFileSelected) => {
    if (!Capacitor.isNativePlatform()) {
        return () => { }; // No-op on web
    }

    // Find all file inputs created by Ant Design Upload
    const handleFileInputClick = async (event) => {
        const target = event.target;

        // Check if this is an Ant Design Upload file input
        // Intercept all file inputs on native to provide camera option
        if (target.tagName === 'INPUT' && target.type === 'file') {
            event.preventDefault();
            event.stopPropagation();

            try {
                // Trigger native camera picker with PROMPT to show Camera/Gallery choice
                const image = await Camera.getPhoto({
                    quality: 100, // Max quality before ImgCrop processing
                    allowEditing: false, // Let ImgCrop handle editing
                    resultType: CameraResultType.DataUrl,
                    source: CameraSource.Prompt, // IMPORTANT: Shows "Camera or Gallery?" dialog
                });

                if (image.dataUrl) {
                    // Convert to File object
                    const response = await fetch(image.dataUrl);
                    const blob = await response.blob();
                    const format = image.format || 'jpeg';
                    const fileName = `photo_${Date.now()}.${format}`;
                    const file = new File([blob], fileName, { type: `image/${format}` });

                    // Trigger the onFileSelected callback
                    if (onFileSelected) {
                        onFileSelected(file);
                    }

                    // Also trigger the input's change event for Ant Design Upload
                    const dataTransfer = new DataTransfer();
                    dataTransfer.items.add(file);
                    target.files = dataTransfer.files;

                    const changeEvent = new Event('change', { bubbles: true });
                    target.dispatchEvent(changeEvent);
                }
            } catch (error) {
                // User cancelled or error occurred
                console.log('Native picker cancelled or failed:', error);
            }
        }
    };

    // Add event listener to capture file input clicks
    document.addEventListener('click', handleFileInputClick, true);

    // Return cleanup function
    return () => {
        document.removeEventListener('click', handleFileInputClick, true);
    };
};
